import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { ReactNode, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { MdDeleteOutline } from 'react-icons/md';
import { DatePicker } from '@Components/DatePicker';
import { Appearance, Design, Placement, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Header } from '@Components/StickyTable';
import { CompanyFiscalCal } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { getFormattedValue } from '@Components/NumberFormatter/NumberFormatter';
import {
  ALLOW_ALL_DATES,
  GoalRow,
  GoalSelectorOptions,
  MULTI_VALUE_SEGMENTS,
  OptionsKey
} from './goal-intake-constants';
import { TableItem } from './goal-intake-edit-utils';
import {
  isChannelOptionDisabled,
  isDecimalGoal,
  mixesPacingChannels,
  requiresKpi
} from './goal-intake-utils';

interface RowEditProps {
  row: GoalRow;
  onChange: (row: GoalRow) => void;
}

interface SelectCellProps {
  field: string;
  optionsKey: string;
  isMulti: boolean;
  row: GoalRow;
  selectorOptions: GoalSelectorOptions;
  hasError: boolean;
  onChange: (row: GoalRow) => void;
}

// Menus render fixed at document level so the table's horizontal scroll doesn't clip them.
const MENU_PROPS = { menuPosition: 'fixed' as const, isValidNewOption: () => false };

// Judged against the dropdown's live selection on every render, so the other group opens up the
// moment the last checked option is cleared.
const CHANNEL_MENU_PROPS = {
  ...MENU_PROPS,
  isOptionDisabled: (option: { value: string }, selected: readonly { value: string }[]) =>
    isChannelOptionDisabled(
      selected.map(({ value }) => value),
      option.value
    )
};

const SelectCell = ({
  field,
  optionsKey,
  isMulti,
  row,
  selectorOptions,
  hasError,
  onChange
}: SelectCellProps) => {
  const current = row[field];
  const options = useMemo(() => {
    const mapped = (selectorOptions[optionsKey] ?? []).map(({ label, value }) => ({
      label: String(label),
      value: String(value)
    }));
    // The API returns these options unsorted.
    return SORTED_OPTIONS_KEYS.has(optionsKey)
      ? mapped.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      : mapped;
  }, [selectorOptions, optionsKey]);
  const common = {
    size: Size.MD,
    options,
    searchable: true as const,
    clearable: true,
    hasError,
    menuPortalTarget: document.body,
    reactSelectProps: field === 'Channel' ? CHANNEL_MENU_PROPS : MENU_PROPS,
    ariaLabel: field
  };
  return isMulti ? (
    <Dropdown
      {...common}
      multiSelect
      // Chips show every selected value in full instead of "N items selected".
      showChips
      value={Array.isArray(current) ? current.map(String) : []}
      onChange={values => onChange({ ...row, [field]: values.length > 0 ? values : null })}
    />
  ) : (
    <Dropdown
      {...common}
      value={current == null || Array.isArray(current) ? null : String(current)}
      onChange={(value: string | null) => onChange({ ...row, [field]: value })}
    />
  );
};

// Dollar goals take cash-register entry (digits fill in from the cents place); ratio and count
// goals (ROAS, CTR, KPI Volume) take a plain number.
export const GoalValueField = ({ row, onChange }: RowEditProps) => {
  const value = row.goal_value;
  const setValue = (goalValue: number | null) => onChange({ ...row, goal_value: goalValue });

  if (isDecimalGoal(row.goal_name)) {
    return (
      <Form.Control
        className='goal-intake-new-value-input'
        type='number'
        data-testid='field-goal_value'
        value={value ?? ''}
        onChange={e => {
          const parsed = parseFloat(e.target.value);
          setValue(Number.isNaN(parsed) ? null : parsed);
        }}
      />
    );
  }

  return (
    <Form.Control
      className='goal-intake-new-value-input'
      data-testid='field-goal_value'
      value={value == null ? '' : getFormattedValue({ value, type: '$', decimals: 2 })}
      onChange={e => {
        const cents = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10);
        setValue(Number.isNaN(cents) ? null : cents / 100);
      }}
    />
  );
};

// Option lists sorted by label; the other fields keep their order (Goal Type is a fixed list).
const SORTED_OPTIONS_KEYS: ReadonlySet<string> = new Set([
  'Channel',
  OptionsKey.KPI,
  OptionsKey.CAMPAIGN,
  OptionsKey.AD_GROUP,
  OptionsKey.SOURCE
]);

// Fits the slim date button (16px icon + 16px caret + 4px gaps + 64px for the widest date text +
// 12px padding + 2px border = 118px) and the row's 8px side padding. The wider marker labels
// ("Week of May 20, 2020", 174px) spill over the neighbouring marker cells instead.
const DATE_COLUMN_WIDTH = 136;
const STANDARD_CALENDAR: CompanyFiscalCal = {};

interface GoalDateFieldProps {
  date: string;
  hasError?: boolean;
  onChange: (date: string) => void;
}

export const GoalDateField = ({ date, hasError, onChange }: GoalDateFieldProps) => (
  <DatePicker
    isSingle
    numVisibleMonths={1}
    hideDateInput
    // An empty override is a standard calendar; it also drops the calendar-settings wrench.
    fiscalCalOverride={STANDARD_CALENDAR}
    size={Size.MD}
    stretch={StretchMode.FILL}
    appearance={Appearance.OUTLINED}
    design={Design.NEUTRAL}
    placeholder='mm/dd/yyyy'
    range={{ start: date, end: date }}
    isOutsideRange={ALLOW_ALL_DATES}
    hasError={hasError}
    onChange={({ start }) => onChange(start)}
  />
);

// Like the old Expected Bookings page, a legacy row's amount can be edited and the row deleted;
// its dates, Channel and goal type are fixed. These columns render like any other row.
const LEGACY_EDITABLE_COLUMNS: ReadonlySet<string> = new Set([
  'legacy_marker',
  'goal_value',
  'delete'
]);
const MARKER_COLUMN_WIDTH = 40;

export const getLegacyTooltip = (row: GoalRow): ReactNode => (
  <>
    This booking was carried over from the old Expected Bookings page. You can edit the goal value
    or delete the row. The dates, channel and goal type are fixed.
    {row.legacyConflict && (
      <>
        <br />
        This amount takes priority over the other entry for the same week and channel.
      </>
    )}
  </>
);

const WarningMarker = ({ testId, children }: { testId: string; children: ReactNode }) => (
  <span className='goal-intake-new-legacy-marker' data-testid={testId}>
    <InfoTooltip placement={Placement.RIGHT}>{children}</InfoTooltip>
  </span>
);

export const LegacyMarker = ({ row }: { row: GoalRow }) => (
  <WarningMarker testId='goal-intake-new-legacy-marker'>{getLegacyTooltip(row)}</WarningMarker>
);

export const MixedChannelMarker = () => (
  <WarningMarker testId='goal-intake-new-mixed-channel-marker'>
    This budget combines channels that have a streaming pacing page with other channels. Streaming
    Video, Video and Streaming Audio budgets should be entered on their own.
  </WarningMarker>
);

// What a legacy (expected_bookings) row shows in the columns that are not editable.
const getLegacyContent = (name: string, row: GoalRow): ReactNode => {
  switch (name) {
    case 'start_date':
    case 'end_date': {
      const date = row[name];
      // Same format as the DatePicker buttons on editable rows.
      return typeof date === 'string' ? Dfns.format('M/d/yy')(Dfns.parseISO(date)) : null;
    }
    default: {
      const value = row[name];
      if (Array.isArray(value)) return value.join(', ');
      return value == null ? null : String(value);
    }
  }
};

type ColumnDef = Omit<Header, 'renderer'> & { renderer: (item: TableItem) => ReactNode };

interface BuildHeadersArgs {
  customSegments: string[];
  selectorOptions: GoalSelectorOptions;
  unsavedKeys: Set<string>;
  errors: Map<string, string>;
  onChange: (key: string, row: GoalRow) => void;
  onDelete: (key: string) => void;
}

// Columns in order: view group, dates, the company's segments, goal type/value, KPI + source,
// campaign, ad group, error, delete. Editable cells follow Segmentation Mapping's pattern: a
// CELL-variant Dropdown rendered from the header's renderer.
export const buildGoalHeaders = ({
  customSegments,
  selectorOptions,
  unsavedKeys,
  errors,
  onChange,
  onDelete
}: BuildHeadersArgs): Header[] => {
  const cell = (draft: TableItem, content: ReactNode) => (
    <div className={cn('goal-intake-new-cell', { 'unsaved-cell': unsavedKeys.has(draft.key) })}>
      {content}
    </div>
  );
  const select = (draft: TableItem, field: string, optionsKey: string, isMulti: boolean) =>
    cell(
      draft,
      <SelectCell
        field={field}
        optionsKey={optionsKey}
        isMulti={isMulti}
        row={draft.row}
        selectorOptions={selectorOptions}
        hasError={errors.has(draft.key)}
        onChange={row => onChange(draft.key, row)}
      />
    );
  const kpiSelect = (draft: TableItem, field: string, optionsKey: string) =>
    requiresKpi(draft.row) ? select(draft, field, optionsKey, false) : cell(draft, null);

  const columns: ColumnDef[] = [
    {
      label: '',
      name: 'legacy_marker',
      width: MARKER_COLUMN_WIDTH,
      nonInteractive: true,
      renderer: (draft: TableItem) =>
        draft.row.legacy || mixesPacingChannels(draft.row) ? (
          <div className='goal-intake-new-cell'>
            {draft.row.legacy ? <LegacyMarker row={draft.row} /> : <MixedChannelMarker />}
          </div>
        ) : null
    },
    ...(['start_date', 'end_date'] as const).map(field => ({
      label: field === 'start_date' ? 'Start Date' : 'End Date',
      name: field,
      width: DATE_COLUMN_WIDTH,
      nonInteractive: true,
      renderer: (draft: TableItem) => {
        const date = draft.row[field] ?? '';
        return cell(
          draft,
          <GoalDateField
            date={date}
            hasError={errors.has(draft.key)}
            onChange={start => onChange(draft.key, { ...draft.row, [field]: start })}
          />
        );
      }
    })),
    ...customSegments.map(segment => ({
      label: MULTI_VALUE_SEGMENTS.has(segment) ? `${segment}(s)` : segment,
      name: segment,
      flex: true,
      // Multi-value segments (Channel) need room for several values.
      minFlexWidth: MULTI_VALUE_SEGMENTS.has(segment) ? 320 : 200,
      nonInteractive: true,
      renderer: (draft: TableItem) =>
        select(draft, segment, segment, MULTI_VALUE_SEGMENTS.has(segment))
    })),
    {
      label: 'Goal Type',
      name: 'goal_name',
      flex: true,
      minFlexWidth: 200,
      nonInteractive: true,
      renderer: (draft: TableItem) => select(draft, 'goal_name', OptionsKey.GOAL_NAME, false)
    },
    {
      label: 'Goal Value',
      name: 'goal_value',
      width: 180,
      nonInteractive: true,
      renderer: (draft: TableItem) =>
        cell(draft, <GoalValueField row={draft.row} onChange={row => onChange(draft.key, row)} />)
    },
    {
      label: 'KPI',
      name: 'kpi',
      flex: true,
      minFlexWidth: 240,
      nonInteractive: true,
      renderer: (draft: TableItem) => kpiSelect(draft, 'kpi', OptionsKey.KPI)
    },
    {
      label: 'KPI Source',
      name: 'source',
      flex: true,
      minFlexWidth: 170,
      nonInteractive: true,
      renderer: (draft: TableItem) => kpiSelect(draft, 'source', OptionsKey.SOURCE)
    },
    {
      label: 'Campaign (Platform)',
      name: 'campaign_platform',
      flex: true,
      minFlexWidth: 300,
      nonInteractive: true,
      renderer: (draft: TableItem) => select(draft, 'campaign_platform', OptionsKey.CAMPAIGN, true)
    },
    {
      label: 'Ad Group (Platform)',
      name: 'ad_group_platform',
      flex: true,
      minFlexWidth: 300,
      nonInteractive: true,
      // Read-only: the lambda's setBudgetIntakeData has no ad group column, so an edit would never save.
      renderer: (draft: TableItem) => cell(draft, getLegacyContent('ad_group_platform', draft.row))
    },
    {
      label: 'Issue',
      name: 'error',
      flex: true,
      minFlexWidth: 300,
      nonInteractive: true,
      renderer: (draft: TableItem) => (
        <div className='goal-intake-new-row-issue'>
          <span className='goal-intake-new-row-error' data-testid='goal-intake-new-row-error'>
            {errors.get(draft.key)}
          </span>
          {requiresKpi(draft.row) && (
            <span className='goal-intake-new-kpi-hint'>KPI not listed? ask Client Reporting</span>
          )}
        </div>
      )
    },
    {
      label: '',
      name: 'delete',
      width: 56,
      nonInteractive: true,
      renderer: (draft: TableItem) => (
        <IconButton
          appearance={Appearance.EMPTY}
          tooltip={{ text: 'Delete' }}
          data-testid='goal-intake-new-delete-row'
          onClick={() => onDelete(draft.key)}
        >
          <MdDeleteOutline />
        </IconButton>
      )
    }
  ];

  // Marker rows fill every column grey; the label sits in the first column.
  return columns.map((header, index) => ({
    ...header,
    renderer: (item: TableItem) =>
      item.markerLabel === undefined ? (
        <div className={cn('goal-intake-new-row', { striped: item.isStriped })}>
          {item.row.legacy && !LEGACY_EDITABLE_COLUMNS.has(header.name ?? '') ? (
            <div className='goal-intake-new-cell goal-intake-new-legacy-cell'>
              {getLegacyContent(header.name ?? '', item.row)}
            </div>
          ) : (
            header.renderer(item)
          )}
        </div>
      ) : (
        <div className={cn('goal-intake-new-marker', { 'first-marker': index === 0 })}>
          {index === 0 ? item.markerLabel : null}
        </div>
      )
  }));
};
